/**
* Responsibility:
* - Parse HTTP `Retry-After` header into client delay milliseconds.
*
* Touches:
* - Reads response headers and interprets seconds/date formats.
*
* Guarantees:
* - Returns non-negative delay or `0` when unavailable.
*/
export function parseRetryAfterMs(response) {
	const rawHeader = String(response?.headers?.get('retry-after') || '').trim();
	if (rawHeader === '') {
		return 0;
	}

	if (/^\d+$/.test(rawHeader)) {
		const seconds = Number.parseInt(rawHeader, 10);
		if (Number.isFinite(seconds) && seconds > 0) {
			return seconds * 1000;
		}
		return 0;
	}

	const retryAtMs = Date.parse(rawHeader);
	if (!Number.isFinite(retryAtMs)) {
		return 0;
	}

	const diff = retryAtMs - Date.now();
	return diff > 0 ? diff : 0;
}

/**
* Responsibility:
* - Resolve promise after the provided delay.
*
* Touches:
* - Uses `window.setTimeout()` for timing.
*
* Guarantees:
* - Resolves immediately for non-positive delays.
*/
export function sleepMs(delayMs) {
	const safeDelay = Number.isFinite(delayMs) ? Number(delayMs) : 0;
	if (safeDelay <= 0) {
		return Promise.resolve();
	}

	return new Promise((resolve) => {
		window.setTimeout(resolve, safeDelay);
	});
}

/**
* Responsibility:
* - Resolve bounded retry delay from server hints and exponential backoff.
*
* Touches:
* - Uses `error.retryAfterMs` when available; otherwise applies jittered backoff.
*
* Guarantees:
* - Returns non-negative bounded retry delay in milliseconds.
*/
export function resolveRetryDelayMs(error, attempt, options = {}) {
	const maxDelayMs = Number.isFinite(options.maxDelayMs) ? Number(options.maxDelayMs) : 2500;
	const baseDelayMs = Number.isFinite(options.baseDelayMs) ? Number(options.baseDelayMs) : 500;
	const jitterMaxMs = Number.isFinite(options.jitterMaxMs) ? Number(options.jitterMaxMs) : 250;

	const retryAfterMs = Number(error?.retryAfterMs || 0);
	if (Number.isFinite(retryAfterMs) && retryAfterMs > 0) {
		return Math.min(retryAfterMs, maxDelayMs);
	}

	const safeAttempt = Number.isFinite(attempt) && attempt > 0 ? Math.trunc(attempt) : 1;
	const backoff = baseDelayMs * (2 ** (safeAttempt - 1));
	const jitter = Math.floor(Math.random() * Math.max(0, jitterMaxMs));

	return Math.min(backoff + jitter, maxDelayMs);
}

/**
* Responsibility:
* - Generate and store next request token for form-scoped in-flight tracking.
*
* Touches:
* - Mutates `form.dataset[seqKey]` and `form.dataset[activeKey]`.
*
* Guarantees:
* - Returns new token string and marks it active.
*/
export function nextRequestToken(form, seqKey, activeKey) {
	const current = Number.parseInt(String(form?.dataset?.[seqKey] || '0'), 10);
	const next = Number.isFinite(current) ? current + 1 : 1;
	const token = String(next);
	form.dataset[seqKey] = token;
	form.dataset[activeKey] = token;
	return token;
}

/**
* Responsibility:
* - Invalidate current request token for a form by advancing sequence.
*
* Touches:
* - Delegates token update through `nextRequestToken()`.
*
* Guarantees:
* - Any previous token becomes stale.
*/
export function invalidateRequest(form, seqKey, activeKey) {
	nextRequestToken(form, seqKey, activeKey);
}

/**
* Responsibility:
* - Verify token still matches currently active request token.
*
* Touches:
* - Reads `form.dataset[activeKey]`.
*
* Guarantees:
* - Returns `true` only when token equals active token.
*/
export function isRequestCurrent(form, activeKey, token) {
	return String(form?.dataset?.[activeKey] || '') === String(token);
}
